import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import { HMDA, IncomeSourcesDisclosure, SMSCharges, WaCivilUnionDisclosure } from "src/components";
import { Page } from "src/components/FlowLayout";
import { PageStateConverter } from "src/hooks/usePageState";
import { Extractor } from "src/hooks/useReducerPageState";
import { selectApplicationLocation } from "src/selectors";
import { Applicant, IncomeSource } from "src/types/Applicant";

import { enums, formatters, isTruthy, parsers, pick } from "@fraction/shared";

import Assets from "../Assets/Overview";
import Debts from "../Debts/Overview";
import IncomeSources from "../IncomeSourcePages/Overview";
import OtherOwnedProperties from "../OtherOwnedProperties/Overview";
import {
  ApplicantAddress,
  ApplicantPhone,
  ApplicantSex,
  BankruptcyStatus,
  DateOfBirth,
  Ethnicity,
  MaritalStatus,
  Race,
  ResidencyStatus,
  SocialXNumber,
  TimeSinceBankruptcy,
} from "./pages";
import EmploymentStatus from "./pages/EmploymentStatus";
import WaCivilUnionAddendum from "./pages/WaCivilUnionAddendum";

export type ApplicantInformationSectionArguments = PageStateConverter<
  Applicant,
  | "address"
  | "name"
  | "email"
  | "phoneAndContactMethodPreferences"
  | "dependents"
  | "race"
  | "ethnicity"
  | "sex"
  | "militaryStatus"
  | "maritalStatus"
  | "socialXNumber"
  | "residencyStatus"
  | "employmentStatus"
  | "bankruptcyStatus"
  | "timeSinceBankruptcy"
  | "dateOfBirth"
  | "waCivilUnionAddendum"
> & { applicant: Applicant };

export type SharedItemsArguments = PageStateConverter<
  ApplicationFlowState,
  "debts" | "assets" | "otherOwnedProperties" | "incomeSources"
>;

const MINIMUM_INCOME_TO_NOT_NEED_ASSETS = 25000;

const ApplicantInformationPages = (
  applicantItems: ApplicantInformationSectionArguments,
  sharedItems: SharedItemsArguments,
  reducerState: {
    extractor: Extractor<ApplicationFlowState>;
    state: ApplicationFlowState;
    basePropertyPath: (string | number)[];
  },
  idVerificationPages?: Page[]
): Page[] => {
  const {
    address,
    phoneAndContactMethodPreferences,
    employmentStatus,
    bankruptcyStatus,
    timeSinceBankruptcy,
    applicant,
    race,
    ethnicity,
    sex,
    socialXNumber,
    residencyStatus,
    dateOfBirth,
    maritalStatus,
    waCivilUnionAddendum,
  } = applicantItems;

  const moreThanOneApplicant = (reducerState.state.applicants?.length || 0) > 1;
  const otherApplicants =
    reducerState.state.applicants?.filter((a) => applicant.email && a.email && a.email !== applicant.email) ||
    [];
  const { country, isUSA, isCanada, isWashington } = selectApplicationLocation(
    reducerState.state.propertyLocation
  );
  const primaryPropertyUsage = reducerState.state.propertyType?.propertyUsage;
  const isPrimaryResidence = primaryPropertyUsage === enums.PropertyUsage.PRIMARY_RESIDENCE;

  const firstName = parsers.user.parseName(applicant?.name || "").first;
  const possessiveFirstName = formatters.user.possessive(firstName);

  const hasEnoughIncome =
    (sharedItems.incomeSources?.value?.items?.reduce(
      (a: number, b: IncomeSource) =>
        a + formatters.number.getNumberFromString(b?.incomeDetails?.income || 0),
      0
    ) || 0) >= MINIMUM_INCOME_TO_NOT_NEED_ASSETS;

  return [
    {
      name: phoneAndContactMethodPreferences.name,
      component: (
        <ApplicantPhone
          title={moreThanOneApplicant ? `Where should we call ${firstName}?` : "Where should we call you?"}
          applicantPriority={applicant.priority}
          {...phoneAndContactMethodPreferences}
        >
          <SMSCharges />
        </ApplicantPhone>
      ),
    },
    // If there is only one applicant and they listed the property as the primary residence,
    // no need to ask where they live
    !(!moreThanOneApplicant && isPrimaryResidence) && {
      name: address.name,
      component: (
        <ApplicantAddress
          loanAddress={reducerState.state?.propertyLocation}
          title={moreThanOneApplicant ? `Where does ${firstName} live?` : "Where do you live?"}
          {...address}
        />
      ),
    },
    isUSA &&
      isPrimaryResidence && {
        name: race.name,
        component: (
          <Race
            title={moreThanOneApplicant ? `What is ${possessiveFirstName} race?` : "What is your race?"}
            {...race}
          >
            <HMDA />
          </Race>
        ),
      },
    isUSA && {
      name: maritalStatus.name,
      component: (
        <MaritalStatus firstName={firstName} moreThanOneApplicant={moreThanOneApplicant} {...maritalStatus}>
          <HMDA />
        </MaritalStatus>
      ),
      pageNeedsToBeFilled: !maritalStatus.value?.maritalStatus,
    },
    isWashington && {
      name: waCivilUnionAddendum.name,
      component: (
        <WaCivilUnionAddendum
          firstName={firstName}
          otherApplicants={otherApplicants}
          {...waCivilUnionAddendum}
        >
          <WaCivilUnionDisclosure />
          <HMDA />
        </WaCivilUnionAddendum>
      ),
      pageNeedsToBeFilled:
        !waCivilUnionAddendum.value?.waCivilUnionAddendum || !waCivilUnionAddendum.value?.connectedToName,
    },
    isUSA &&
      isPrimaryResidence && {
        name: sex.name,
        component: (
          <ApplicantSex
            title={moreThanOneApplicant ? `What is ${possessiveFirstName} legal sex?` : "What is your sex?"}
            {...sex}
          >
            <HMDA />
          </ApplicantSex>
        ),
      },
    isUSA &&
      isPrimaryResidence && {
        name: ethnicity.name,
        component: (
          <Ethnicity
            title={
              moreThanOneApplicant ? `What is ${possessiveFirstName} ethnicity?` : "What is your ethnicity?"
            }
            {...ethnicity}
          >
            <HMDA />
          </Ethnicity>
        ),
      },
    {
      name: residencyStatus.name,
      component: (
        <ResidencyStatus
          applicantId={applicant.id}
          title={
            isCanada
              ? moreThanOneApplicant
                ? `Is ${firstName} a Canadian citizen or permanent resident?`
                : "Are you a Canadian citizen or permanent resident?"
              : moreThanOneApplicant
              ? `What is ${possessiveFirstName} residency status?`
              : "What is your residency status?"
          }
          location={{ country }}
          {...residencyStatus}
        />
      ),
    },
    {
      name: dateOfBirth.name,
      component: (
        <DateOfBirth
          title={
            moreThanOneApplicant
              ? `What is ${possessiveFirstName} date of birth?`
              : "What is your date of birth?"
          }
          {...dateOfBirth}
        />
      ),
    },
    (residencyStatus.value === enums.ResidencyStatus.CA_CITIZEN_OR_PERMANENT_RESIDENT ||
      residencyStatus.value === enums.ResidencyStatus.US_CITIZEN ||
      residencyStatus.value === enums.ResidencyStatus.US_PERMANENT_RESIDENT_ALIEN) && {
      name: socialXNumber.name,
      component: (
        <SocialXNumber
          title={`What is ${moreThanOneApplicant ? possessiveFirstName : "your"} ${
            isCanada ? "Social Insurance Number" : "Social Security Number"
          }?`}
          location={{ country }}
          {...socialXNumber}
        />
      ),
      pageNeedsToBeFilled: !socialXNumber.value,
    },
    ...(idVerificationPages || []),
    {
      name: `${sharedItems.otherOwnedProperties.name}-${applicant?.id || applicant?.email?.email}`,
      component: (
        <OtherOwnedProperties
          applicant={applicant}
          title={
            moreThanOneApplicant
              ? `Does ${firstName} have other properties?`
              : "Do you have other properties?"
          }
          {...reducerState}
          {...sharedItems.otherOwnedProperties}
          value={{
            ...sharedItems.otherOwnedProperties.value,
            items:
              sharedItems.otherOwnedProperties.value?.items?.map?.((item) =>
                pick(item, ["propertyLocation", "propertyType", "propertyValue", "id"])
              ) || [],
          }}
          blockFocus="start"
        />
      ),
      pageNeedsToBeFilled: false,
    },
    {
      name: `${sharedItems.incomeSources.name}-${applicant?.id || applicant?.email?.email}`,
      component: (
        <IncomeSources
          title={
            moreThanOneApplicant
              ? `What are ${possessiveFirstName} sources of income?`
              : "What are your sources of income?"
          }
          {...sharedItems.incomeSources}
          blockFocus="start"
        >
          {isUSA && <IncomeSourcesDisclosure />}
        </IncomeSources>
      ),
      pageNeedsToBeFilled: !sharedItems.incomeSources.value?.items.length,
    },
    !hasEnoughIncome && {
      name: `${sharedItems.assets.name}-${applicant?.id || applicant?.email?.email}`,
      component: (
        <Assets
          applicant={applicant}
          title={moreThanOneApplicant ? `Does ${firstName} have other assets?` : "Do you have other assets?"}
          {...sharedItems.assets}
          blockFocus="start"
        />
      ),
      pageNeedsToBeFilled: false,
    },
    {
      name: `${sharedItems.debts.name}-${applicant?.id || applicant?.email?.email}`,
      component: (
        <Debts
          applicant={applicant}
          title={moreThanOneApplicant ? `Does ${firstName} have other debts?` : "Do you have other debts?"}
          {...sharedItems.debts}
          blockFocus="start"
        />
      ),
      pageNeedsToBeFilled: false,
    },
    {
      name: employmentStatus.name,
      component: (
        <EmploymentStatus
          title={
            moreThanOneApplicant ? `What's ${firstName} employment status?` : `What's your employment status?`
          }
          {...employmentStatus}
        />
      ),
    },
    {
      name: bankruptcyStatus.name,
      component: (
        <BankruptcyStatus
          title={
            moreThanOneApplicant
              ? `Has ${firstName} declared bankruptcy in the past?`
              : "Have you declared bankruptcy in the past?"
          }
          {...bankruptcyStatus}
        />
      ),
    },
    bankruptcyStatus?.value !== enums.BankruptcyStatus.NEVER && {
      name: timeSinceBankruptcy.name,
      component: (
        <TimeSinceBankruptcy
          applicantId={applicant.id}
          title={
            moreThanOneApplicant
              ? `How long ago did ${firstName} declare bankruptcy?`
              : "How long ago did you declare bankruptcy?"
          }
          {...timeSinceBankruptcy}
        />
      ),
    },
  ].filter(isTruthy);
};

export default ApplicantInformationPages;
